import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaCheckCircle, FaExclamationCircle, FaBuilding, FaSpinner, FaTrashAlt, FaArrowLeft } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

const initialCompanyState = {
  company_name: '',
  address: '',
  pancard: '',
  city: '',
  state: '',
  country: '',
  gst_number: '',
  gst_uin: '',
  code: '',
  cin: '',
  email: '',
};

type CompanyType = typeof initialCompanyState & { id?: string | number; bank_id?: string | number };

const fieldGroups: Array<{ title?: string, icon?: any, fields: Array<any> }> = [
  {
    title: "Basic Info",
    icon: FaBuilding,
    fields: [
      { label: "Company Name", name: "company_name", required: true, icon: <FaBuilding className="inline mr-2 text-blue-700" /> },
      { label: "Address", name: "address", textarea: true },
      { label: "Email", name: "email", inputType: "email" },
      { label: "City", name: "city" },
      { label: "State", name: "state" },
      { label: "Country", name: "country" },
    ]
  },
  {
    title: "Registration",
    icon: null,
    fields: [
      { label: "PAN", name: "pancard" },
      { label: "GST Number", name: "gst_number" },
      { label: "GST UIN", name: "gst_uin" },
      { label: "Code", name: "code" },
      { label: "CIN", name: "cin" },
    ]
  }
];

const AddCompany: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyId = searchParams.get('company_id');
  const isEditMode = companyId && companyId !== 'new';

  const [company, setCompany] = useState<CompanyType>(initialCompanyState);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const alertRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error || formError || success) {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [error, formError, success]);

  useEffect(() => {
    // If we have a valid companyId (not 'new'), it's edit mode, fetch company details
    if (isEditMode && companyId) {
      setLoading(true);
      axios
        .get(`${API_URL}/auth/company/${companyId}`)
        .then((res) => {
          setCompany(res.data);
        })
        .catch(() => setError('Unable to load company details.'))
        .finally(() => setLoading(false));
    } else {
      setCompany(initialCompanyState);
    }
    // eslint-disable-next-line
  }, [companyId, isEditMode]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCompany((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccess('');
    if (!company.company_name.trim()) {
      setFormError('Company name is required.');
      return;
    }
    if (company.email && !validateEmail(company.email)) {
      setFormError('Invalid email address.');
      return;
    }
    setSubmitting(true);
    try {
      let response: any;
      if (isEditMode && companyId) {
        response = await axios.put(`${API_URL}/auth/company/${companyId}`, company);
        setSuccess('Company updated successfully!');
      } else {
        response = await axios.post(`${API_URL}/auth/company`, company);
        setSuccess('Company added successfully!');
        setCompany(initialCompanyState);
      }
      // After success, redirect after short timeout
      setTimeout(() => {
        navigate('/company');
      }, 1200);
    } catch (err: any) {
      if (err.response?.data?.error) {
        setFormError(err.response.data.error);
      } else {
        setFormError('Failed to save company.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !companyId) return;
    setDeleteLoading(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/auth/company/${companyId}`);
      setSuccess('Deleted successfully!');
      setTimeout(() => {
        navigate('/company');
      }, 900);
    } catch {
      setError('Failed to delete company.');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto bg-gradient-to-tr from-blue-50 via-slate-50 to-fuchsia-50 shadow-2xl rounded-xl p-6 mt-8 border border-blue-100 relative">
      <h2 className="text-3xl font-extrabold mb-2 flex items-center gap-2 text-blue-700 drop-shadow-sm">
        {isEditMode ? 'Edit Company' : 'Add Company'}
      </h2>
      <p className="mb-5 text-gray-500 text-sm">
        Please fill in the company details below. Your information is secure and confidential.
      </p>
      <div ref={alertRef}>
        {error && <Alert type="error" message={error} />}
        {formError && <Alert type="error" message={formError} />}
        {success && <Alert type="success" message={success} />}
      </div>

      <div className="flex flex-col md:flex-row gap-8 w-full">
        <div className="w-full md:w-1/2 bg-white border border-blue-100 px-4 py-8 rounded-lg shadow flex flex-col">
          <h3 className="font-semibold text-blue-700 flex items-center gap-2 mb-4 text-xl">
            <FaBuilding /> Company Details
          </h3>
          {loading ? (
            <div className="flex justify-center items-center text-blue-700 font-semibold animate-pulse py-6">
              <FaSpinner className="animate-spin mr-2" /> Loading company...
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col space-y-4 animate__animated animate__fadeIn">
              {fieldGroups.map((group, idx) => (
                <fieldset key={group.title || idx} className="rounded-lg mb-2 px-1 py-1 border-0">
                  {group.title && (
                    <legend className="font-semibold text-blue-700 flex items-center gap-2 mb-2 text-base">
                      {group.icon && <group.icon />}
                      {group.title}
                    </legend>
                  )}
                  <div className="grid grid-cols-1 gap-y-2">
                    {group.fields.map((fld: any) => (
                      <FormField
                        key={fld.name}
                        label={fld.label}
                        name={fld.name}
                        required={fld.required}
                        value={company[fld.name as keyof typeof initialCompanyState] as string}
                        onChange={handleInputChange}
                        textarea={fld.textarea}
                        inputType={fld.inputType}
                        icon={fld.icon}
                      />
                    ))}
                  </div>
                </fieldset>
              ))}

              <div className="flex gap-2 pt-2 justify-between mt-2">
                <button
                  type="submit"
                  className="px-7 py-2 bg-gradient-to-r from-blue-600 to-fuchsia-600 text-white font-bold rounded-lg shadow hover:scale-105 transition text-base flex items-center gap-2"
                  disabled={submitting}
                >
                  {submitting ? <FaSpinner className="animate-spin inline" /> : ''}
                  {isEditMode
                    ? submitting ? 'Updating...' : 'Update Company'
                    : submitting ? 'Adding...' : 'Add Company'}
                </button>
                <button
                  type="button"
                  className="bg-gray-100 px-6 py-2 rounded-md border border-gray-300 hover:bg-gray-200 font-medium transition text-gray-700 flex items-center gap-2"
                  onClick={() => navigate('/company')}
                  disabled={submitting || deleteLoading}
                >
                  <FaArrowLeft /> Cancel
                </button>
                {isEditMode && (
                  <button
                    type="button"
                    className="ml-auto px-4 py-2 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-md font-semibold shadow hover:scale-105 transition flex items-center gap-2"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={deleteLoading}
                  >
                    <FaTrashAlt />
                    {deleteLoading ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {showDeleteConfirm && (
        <DeleteConfirm
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          loading={deleteLoading}
          message="Are you sure you want to delete this company?"
        />
      )}
    </div>
  );
};

// ALERT COMPONENT
const Alert: React.FC<{ type: 'success' | 'error', message: string }> = ({ type, message }) => {
  const icon =
    type === 'success'
      ? <FaCheckCircle className="mr-2 text-green-600" />
      : <FaExclamationCircle className="mr-2 text-red-600" />;
  const color =
    type === 'success'
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-red-50 text-red-700 border-red-200';
  return (
    <div className={`mb-3 rounded border px-4 py-2 flex items-center shadow-sm font-medium ${color} animate__animated animate__fadeIn`}>
      {icon}
      {message}
    </div>
  );
};

// Reusable FormField component (with subtle focus/validation/animation)
interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  textarea?: boolean;
  inputType?: string;
  required?: boolean;
  icon?: React.ReactNode;
}
const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  onChange,
  textarea = false,
  inputType = 'text',
  required = false,
  icon = null
}) => (
  <div className="flex flex-col relative group">
    <label className="block font-semibold mb-1 text-gray-700" htmlFor={name}>
      {icon}{label}{required && <span className="text-red-600 ml-1">*</span>}
    </label>
    {textarea ? (
      <textarea
        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-blue-500 resize-none transition ring-0 bg-gradient-to-tr from-white via-blue-50 to-fuchsia-50"
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        rows={2}
        required={required}
        spellCheck
        placeholder={`Enter ${label.toLowerCase()}...`}
      />
    ) : (
      <input
        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-blue-500 transition bg-gradient-to-tr from-white via-blue-50 to-fuchsia-50"
        id={name}
        name={name}
        type={inputType}
        required={required}
        value={value}
        onChange={onChange}
        autoComplete="off"
        placeholder={`Enter ${label.toLowerCase()}${required ? ' *' : ''}`}
      />
    )}
    <span className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-600 transition-all group-focus-within:w-full group-focus-within:left-0"></span>
  </div>
);

const DeleteConfirm: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  message?: string;
}> = ({ onConfirm, onCancel, loading, message }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50 animate__animated animate__fadeIn">
    <div className="bg-white p-6 rounded-xl shadow-2xl border border-blue-300 relative min-w-[320px] max-w-xs">
      <div className="mb-5 text-xl font-bold text-red-700 flex items-center gap-2">
        <FaExclamationCircle />
        {message || 'Are you sure you want to delete?'}
      </div>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-1.5 bg-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-400 transition"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-4 py-1.5 bg-gradient-to-r from-red-600 to-pink-500 text-white rounded-md font-semibold shadow hover:scale-105 transition"
        >
          {loading ? <FaSpinner className="animate-spin inline" /> : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

export default AddCompany;